/**
 * MUI MeasuringTape component that replaces the styled-components MeasuringTape
 * 
 * This component provides a Material UI equivalent of the original MeasuringTape component
 * with similar props and functionality.
 */
import React, { useState, useEffect, useRef, Fragment } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getIsMeter, getScale, getDrawingBox } from 'reduxModules/ducks/ui';
import { distanceToString } from 'utilities/format';
import { MeasuringTapeProps, MeasuringTapeStyledProps, TapeMeasurePathProps } from 'types/components/elements';

/**
 * Line-rectangle intersection utility for boundary clipping
 */
const clipLineToRectangle = (x0: number, y0: number, x1: number, y1: number, bbox: any) => {
  const { x0: rectX0, y0: rectY0, x1: rectX1, y1: rectY1 } = bbox;
  
  // Cohen-Sutherland line clipping algorithm
  const INSIDE = 0; // 0000
  const LEFT = 1;   // 0001
  const RIGHT = 2;  // 0010
  const BOTTOM = 4; // 0100
  const TOP = 8;    // 1000

  const computeOutCode = (x: number, y: number) => {
    let code = INSIDE;
    if (x < rectX0) code |= LEFT;
    else if (x > rectX1) code |= RIGHT;
    if (y < rectY0) code |= TOP;
    else if (y > rectY1) code |= BOTTOM;
    return code;
  };

  let outcode0 = computeOutCode(x0, y0);
  let outcode1 = computeOutCode(x1, y1);
  let accept = false;

  while (true) {
    if (!(outcode0 | outcode1)) {
      // Both points inside
      accept = true;
      break;
    } else if (outcode0 & outcode1) {
      // Both points outside same region
      break;
    } else {
      // Calculate intersection point
      let x, y;
      const outcodeOut = outcode0 ? outcode0 : outcode1;

      if (outcodeOut & TOP) {
        x = x0 + (x1 - x0) * (rectY0 - y0) / (y1 - y0);
        y = rectY0;
      } else if (outcodeOut & BOTTOM) {
        x = x0 + (x1 - x0) * (rectY1 - y0) / (y1 - y0);
        y = rectY1;
      } else if (outcodeOut & RIGHT) {
        y = y0 + (y1 - y0) * (rectX1 - x0) / (x1 - x0);
        x = rectX1;
      } else if (outcodeOut & LEFT) {
        y = y0 + (y1 - y0) * (rectX0 - x0) / (x1 - x0);
        x = rectX0;
      }

      if (outcodeOut === outcode0) {
        x0 = x!;
        y0 = y!;
        outcode0 = computeOutCode(x0, y0);
      } else {
        x1 = x!;
        y1 = y!;
        outcode1 = computeOutCode(x1, y1);
      }
    }
  }

  return accept ? { x0, y0, x1, y1 } : null;
};

/**
 * MUI MeasuringTape component
 * 
 * @returns {JSX.Element} MUI MeasuringTape component
 */
const MuiMeasuringTape: React.FC<MeasuringTapeProps> = (props) => {
  const scale = useSelector(getScale);
  const isMeter = useSelector(getIsMeter);
  const { bbox } = useSelector(getDrawingBox);
  const { x: x0, y: y0 } = props.anchor;
  const { x: x1, y: y1 } = props.target;

  // Clip the measuring line to floorplan boundaries
  const clippedLine = clipLineToRectangle(x0, y0, x1, y1, bbox);
  const { x0: clippedX0, y0: clippedY0, x1: clippedX1, y1: clippedY1 } = clippedLine || { x0, y0, x1, y1 };

  const [measuring, setMeasuring] = useState<boolean>(false);
  useEffect(() => {
    props.mouseActive ? setMeasuring(props.mouseActive) : setTimeout(() => setMeasuring(false), 350);
  }, [props.mouseActive]);
  
  // Positioning
  let angle = (Math.atan2((y1 - y0), (x1 - x0)) * 180 / Math.PI);
  let flip = false;
  if (Math.abs(angle) > 90) {
    angle = -(180 - Math.abs(angle)) * Math.sign(angle);
    flip = true;
  }

  // Value
  const distance = distanceToString(Math.hypot((x0 - x1), (y0 - y1)) / scale, isMeter);

  // References for animation
  const distLabelRef = useRef<SVGTextElement>(null);
  const [adjustment, setAdjustment] = useState<number>(0);
  useEffect(() => {
    if (distLabelRef.current) setAdjustment(-distLabelRef.current.getBBox().width);
  }, [distance]);

  const lineLengthRef = useRef<SVGPathElement>(null);
  const [length, setLength] = useState<number | undefined>();
  useEffect(() => {
    if (lineLengthRef.current) setLength(lineLengthRef.current.getTotalLength());
  }, [distance]);

  return (
    <Fragment>
      {measuring && clippedLine && (
        <MeasuringTapePath
          d="M0,0 m-10,-3 a7,7 0 0 1 0 -14 a7,7 0 0 1 0 14 m10,3 v-12 a8,8 1 0 0 -8 -8 h-12 v20 h20 z"
          transform={`translate(${clippedX0} ${clippedY0}) rotate(${angle}) scale(${flip ? -1 : 1} 1)`}
          mouseActive={props.mouseActive}
        />
      )}
      {measuring && clippedLine && (
        <TapeMeasurePath
          ref={lineLengthRef}
          d={`M ${clippedX0} ${clippedY0} L ${clippedX1} ${clippedY1}`}
          style={{
            strokeDasharray: length,
          }}
          mouseActive={props.mouseActive}
          animate={measuring && !props.mouseActive}
          length={length}
        />
      )}
      {props.mouseActive && clippedLine && (
        <DistanceText
          ref={distLabelRef}
          transform={`translate(${clippedX1} ${clippedY1}) rotate(${angle}) translate(${flip ? adjustment - 20 : 5} 0)`}
        >
          {distance}
        </DistanceText>
      )}
    </Fragment>
  );
};

// Styled components using MUI styled API
const MeasuringTapePath = styled('path')<MeasuringTapeStyledProps>(({ theme, mouseActive }) => ({
  stroke: theme.palette.text.primary,
  opacity: mouseActive ? 1 : 0,
  transition: mouseActive ? undefined : 'opacity 50ms linear 300ms',
}));

const TapeMeasurePath = styled('path')<TapeMeasurePathProps>(({ theme, mouseActive, animate, length }) => ({
  stroke: theme.palette.text.primary,
  strokeDashoffset: 0,
  shapeRendering: 'geometricPrecision',
  opacity: mouseActive ? 1 : 0,
  transition: mouseActive ? undefined : 'opacity 10ms linear 190ms',
  ...(animate && {
    animation: `measuringTapeLineAnimation 200ms linear 1`,
    '@keyframes measuringTapeLineAnimation': {
      '0%': { strokeDashoffset: 0 },
      '100%': { strokeDashoffset: length || 0 },
    },
  }),
}));

const DistanceText = styled('text')(({ theme }) => `
  font-size: 16pt;
  text-anchor: left;
  fill: ${theme.palette.text.primary};
`);

export default MuiMeasuringTape;
